import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { t } from '../../i18n';
import { BoltIcon, BoxIcon, HexIcon, PawIcon } from '../icons';
import { imageSrc, type ApiImage } from '../lib/media';
import { openPhotos } from '../lib/photos';
import styles from './ui.module.css';

/**
 * Миниатюра в оформлении Мезы: строчки развёртки поверх и уголки кадра по
 * всем четырём углам — те же, что обрамляют отпечатки на её постерах.
 *
 * Заводилась ради единообразия: до неё предмет и питомец рисовались голым
 * <img> с радиусом 8, а способность — компонентом Photo с радиусом 12.
 *
 * Просмотр на весь экран живёт ЗДЕСЬ, а не отдельным компонентом: иначе
 * приходится выбирать между видом Мезы и возможностью разглядеть фото, и
 * при переносе экранов это уже один раз потерялось. Открывает нативную
 * галерею ВК: в неё уходит полный размер, а на экране лежит превью — тянуть
 * полноразмерные файлы в ленту реестра значит съесть трафик игрока.
 */

/**
 * Чья это вещь. Кадр без файла носит её значок — тот же, что у строки хаба:
 * коробка, лапа, разряд. Заглушка тогда говорит не только «фото нет», но и
 * ЧТО здесь стоит. Не назвали — в кадре сота, знак самой Мезы: так у людей,
 * внешек и анкет, где вещи нет.
 */
export type ThumbKind = 'item' | 'pet' | 'ability';

/**
 * Сторона миниатюры — по месту, где она стоит: 46 в строке очереди, 66 в
 * карточке вещи, 72 в шапке разбора, 112 в одиночном кадре различий.
 */
export type ThumbSize = 'row' | 'card' | 'head' | 'diff';

/**
 * Значок пустого кадра. Размер идёт от размера кадра, а не от вида вещи.
 * Значку вещи дают на четыре пиксела больше: сота нарисована в прямоугольнике
 * 20×22 и занимает его целиком, а коробка, лапа и разряд — в квадрате 20 и с
 * полями внутри, поэтому вровень с ней они выглядят мельче.
 */
function EmptyMark({ kind, size }: { kind: ThumbKind | undefined; size: number }) {
  if (kind === 'item') return <BoxIcon size={size + 4} />;
  if (kind === 'pet') return <PawIcon size={size + 4} />;
  if (kind === 'ability') return <BoltIcon size={size + 4} />;

  return <HexIcon size={size} />;
}

interface ThumbProps {
  /** Достаточно { id }: адрес собирается из идентификатора. */
  image: ApiImage | null;
  /** Чья вещь: по нему выбирается значок заглушки. */
  kind?: ThumbKind;
  /** Где стоит миниатюра: от этого её сторона. Игнорируется при fill. */
  size?: ThumbSize;
  /** Тянуться за колонкой вместо фиксированной стороны: плитка реестра. */
  fill?: boolean;
  /**
   * Только с fill. Квадрат обрезает кадр по краям — это верно в ряду плиток,
   * где разная высота ломала бы строку, и неверно на отдельной карточке
   * внешки: туда приходят смотреть портрет целиком. `hero` — широкая шапка
   * карточки персонажа: кадр обрезан сверху и снизу, чтобы под именем
   * осталось место.
   */
  ratio?: 'square' | 'wide' | 'portrait' | 'natural' | 'hero';
  alt?: string;
  /**
   * Уголки снимают там, где они мешают: на шапке карточки персонажа поверх
   * них лежит имя. Сколько их — решает размер: у плитки во всю колонку все
   * четыре, у миниатюры в строке два по диагонали, иначе от кадра 66 px
   * остаётся одна рамка. Ровно так и в макетах.
   */
  corners?: boolean;
  /**
   * Соседние фото блока. Нативная галерея листается свайпом, поэтому отдаём
   * ей весь набор, а не одну картинку: иначе свайп упирается в пустоту.
   * Не передали — просмотра нет вовсе (у заглушки открывать нечего).
   */
  gallery?: (ApiImage | null | undefined)[];
  /**
   * Слой поверх кадра: значок общежития на портрете реестра. Позиционировать
   * его из экрана нельзя — своего CSS у экранов нет, поэтому слот здесь.
   * Нажатия сквозь него проходят: под ним бывает кнопка просмотра.
   */
  overlay?: ReactNode;
  /**
   * Затемнение снизу под текст поверх кадра. Без него имя ложится на светлые
   * места фотографии и пропадает — а какие они, заранее не знает никто.
   */
  scrim?: boolean;
}

const SHAPES = {
  square: styles.thumbFill,
  wide: styles.thumbWide,
  portrait: styles.thumbPortrait,
  natural: styles.thumbNatural,
  hero: styles.thumbHero,
};

const SIZES: Record<ThumbSize, string> = {
  row: styles.thumbRow,
  card: styles.thumbCard,
  head: styles.thumbHead,
  diff: styles.thumbDiff,
};

export function Thumb({
  image,
  kind,
  size = 'head',
  fill = false,
  ratio = 'square',
  alt = '',
  corners = true,
  gallery,
  overlay,
  scrim = false,
}: ThumbProps) {
  const src = imageSrc(image, 'thumb');

  const body = (
    <>
      {src === undefined ? (
        <div className={clsx(styles.thumbImage, styles.thumbEmpty)}>
          <EmptyMark kind={kind} size={fill ? 33 : 22} />
          {/* Подпись только на крупной плитке: в миниатюре 66 px она не
              помещается и обрезается посреди слова. Один контур соты там и
              так понятен — рядом стоит название вещи. */}
          {fill ? <span className={styles.thumbEmptyLabel}>{t('ui.thumb.empty')}</span> : null}
        </div>
      ) : (
        <img className={styles.thumbImage} src={src} alt={alt} loading="lazy" />
      )}

      <span className={styles.thumbScan} />

      {scrim ? <span className={styles.thumbScrim} /> : null}

      {corners ? (
        <>
          <span className={clsx(styles.thumbCorner, styles.thumbCornerTopLeft)} />
          {fill ? <span className={clsx(styles.thumbCorner, styles.thumbCornerTopRight)} /> : null}
          {fill ? (
            <span className={clsx(styles.thumbCorner, styles.thumbCornerBottomLeft)} />
          ) : null}
          <span className={clsx(styles.thumbCorner, styles.thumbCornerBottomRight)} />
        </>
      ) : null}

      {overlay === undefined ? null : (
        <span className={clsx(styles.thumbOverlay, scrim && styles.thumbOverlayWide)}>
          {overlay}
        </span>
      )}
    </>
  );

  const box = clsx(styles.thumb, !fill && styles.thumbFixed, fill ? SHAPES[ratio] : SIZES[size]);

  if (gallery === undefined || src === undefined) {
    return <div className={box}>{body}</div>;
  }

  const list = gallery.filter((item): item is ApiImage => Boolean(item?.id));
  const index = Math.max(
    0,
    list.findIndex((item) => item.id === image?.id),
  );
  const full = list
    .map((item) => imageSrc(item, 'full'))
    .filter((url): url is string => url !== undefined);

  // Настоящая кнопка, а не <img role="button">: у картинки эта роль запрещена
  // правилами доступности, и Tab её всё равно обходил бы.
  return (
    <button
      type="button"
      className={clsx(styles.reset, box)}
      aria-label={alt === '' ? t('ui.thumb.open') : t('ui.thumb.openNamed', { alt })}
      onClick={() => openPhotos(full, index)}
    >
      {body}
    </button>
  );
}
